import type { PageSlotValue } from "@/agent/pageState/registry";
import type { FunderGroup, FunderWithdrawal } from "@/types/underwriting";

import type { OfferValues } from "./UnderwritingAssessmentDialogs";

/**
 * The analysis dashboard's state as the assistant gets it with a message (`useAgentPageState`).
 * Plain values only, in the shapes the server's application slots accept (borrowers/tools/context.py):
 * month keys, rounded numbers, funder names cut to 80 characters, never free text a user typed.
 * The server drops a whole slot that fails its schema, so a number outside its bounds is left out
 * here instead: the rest of the slot still arrives.
 */

/** Keeps a busy screen well inside the server's 2 KB for all of a message's page state. */
export const MAX_WHAT_IFS = 12;
const FUNDER_CHARS = 80;
const MONTH_KEY = /^\d{4}-\d{2}$/;
const FREQUENCY = /^[a-z][a-z_-]{0,19}$/;
const OFFER_FREQUENCIES = new Set(["daily", "weekly", "monthly"]);
const MAX_MONTHS = 60;

/** The server's bounds for each kind of number (Money, Percent, factor, term). */
type Bounds = readonly [min: number, max: number];
const MONEY: Bounds = [-1e10, 1e10];
const PERCENT: Bounds = [0, 1e6];
const FACTOR: Bounds = [0, 100];
const TERM: Bounds = [0, 10_000];

/** `value` to the cent, or undefined (left out) when it is not a number within `bounds`. */
function within(value: number | null | undefined, [min, max]: Bounds): number | undefined {
  if (value == null || !Number.isFinite(value)) return undefined;
  const rounded = Math.round(value * 100) / 100;
  return rounded >= min && rounded <= max ? rounded : undefined;
}

export interface AnalysisSlotInput {
  source: "statements" | "plaid";
  /** The months the average uses, and the ones it uses by default. */
  avgMonthKeys: string[];
  defaultAvgMonthKeys: string[];
  averageRevenue: number | null;
  leveragePct: number;
  monthlyPayout: number;
  leverageWarnPct: number;
  funderGroups: FunderGroup[];
  /** The user's leverage toggle on a position that is not saved (yet), else undefined. */
  localOverride: (fg: FunderGroup, w: FunderWithdrawal, index: number) => boolean | undefined;
}

export function analysisPageSlot(input: AnalysisSlotInput): PageSlotValue {
  const picked =
    input.avgMonthKeys.length !== input.defaultAvgMonthKeys.length ||
    input.avgMonthKeys.some((key, index) => key !== input.defaultAvgMonthKeys[index]);
  const whatIfs = input.funderGroups
    .flatMap((fg) =>
      fg.withdrawals.map((w, index) => {
        const inLeverage = input.localOverride(fg, w, index);
        const amount = within(w.amount, MONEY);
        if (inLeverage === undefined || amount === undefined) return null;
        const frequency = (w.frequency ?? "").toLowerCase();
        return {
          funder: fg.name.slice(0, FUNDER_CHARS),
          position_type: fg.position_type ?? "mca",
          amount,
          frequency: FREQUENCY.test(frequency) ? frequency : "other",
          in_leverage: inLeverage,
        };
      })
    )
    .filter((whatIf) => whatIf !== null)
    .slice(0, MAX_WHAT_IFS);
  return {
    source: input.source,
    ...(picked && {
      avg_months: input.avgMonthKeys.filter((key) => MONTH_KEY.test(key)).slice(-MAX_MONTHS),
    }),
    average_revenue: within(input.averageRevenue, MONEY),
    leverage_pct: within(input.leveragePct, PERCENT),
    monthly_payout: within(input.monthlyPayout, MONEY),
    leverage_warn_pct: within(input.leverageWarnPct, PERCENT),
    unsaved_leverage_whatifs: whatIfs,
  };
}

export function offerPageSlot(offer: OfferValues): PageSlotValue {
  return {
    funding_amount: within(offer.fundingAmount, MONEY),
    factor: within(offer.factor, FACTOR),
    installment: within(offer.installment, MONEY),
    term: within(offer.term, TERM),
    ...(OFFER_FREQUENCIES.has(offer.frequency) && { frequency: offer.frequency }),
    added_leverage_pct: within(offer.addedLeverage, PERCENT),
  };
}
